import { useEffect, useMemo, useState } from 'react';
import {
  Alert,
  App,
  Button,
  Card,
  Collapse,
  DatePicker,
  Drawer,
  Input,
  Modal,
  Select,
  Space,
  Table,
  Tag,
  Tooltip,
  Typography,
  type TableColumnsType,
} from 'antd';
import { useInfiniteQuery, useMutation, useQueries, useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from '../../api/client';
import { UserName, userText } from '../../lib/listenerName';
import { Can } from '../../rbac/RequirePermission';
import { useUrlState, oneOf, dayParam } from '../../lib/urlState';
import { useDebounced } from '../../lib/useDebounced';
import { QueryError } from '../../lib/QueryState';
import { errMsg } from '../../lib/errors';
import { formatRupees } from '../../lib/money';
import { PaymentDetailView } from './PaymentDetailView';
import { RechargeUserCard } from './RechargeUserCard';
import {
  ALL_STATUSES,
  FAILURE_LABEL,
  STAGE_LABEL,
  STATUS_COLOR,
  STATUS_HELP,
  STATUS_LABEL,
  reasonCodeText,
  stageColor,
  type FailureCode,
  type OrderStatus,
  type PaymentDetail,
  type PaymentStage,
} from './paymentLifecycle';

/** Rows fetched per request. The API caps `limit` at 100. */
const PAGE_SIZE = 50;

export interface OrderRow {
  id: string;
  userId: string;
  userHandle?: string;
  /** The paying user's own Display Name — absent on older APIs. */
  userDisplayName?: string | null;
  amountPaise: string;
  gateway: 'razorpay' | 'google_play';
  /** Razorpay reached through Play's User Choice Billing chooser. Absent on older APIs. */
  ucb?: boolean;
  gatewayOrderId: string;
  packageName: string | null;
  /** Paid coins + snapshotted bonus. Null on a legacy arbitrary-amount recharge. */
  coins: number | null;
  bonusCoins: number;
  status: OrderStatus;
  /** Where the attempt is, or where it stopped. */
  stage: PaymentStage;
  /** Our reason code — only on an attempt that has not (yet) succeeded. */
  failureCode: FailureCode | null;
  /** Only ever set on a status that did NOT credit. */
  failureReason: string | null;
  providerErrorCode: string | null;
  providerPaymentId: string | null;
  currency: string;
  createdAt: string;
  updatedAt: string;
  paidAt: string | null;
  capturedAt: string | null;
  /** When it reached a non-captured terminal state. */
  closedAt: string | null;
}

interface OrdersPage {
  items: OrderRow[];
  nextCursor: string | null;
  total: number;
  filtered: number;
}

type Gateway = OrderRow['gateway'];
const GATEWAYS: readonly Gateway[] = ['razorpay', 'google_play'];
const STAGES = Object.keys(STAGE_LABEL) as PaymentStage[];
const FAILURE_CODES = Object.keys(FAILURE_LABEL) as FailureCode[];

/**
 * Recharge history. Every filter, the search and the open recharge live in the
 * URL (see lib/urlState): Back from a user's page returns to the same list, a
 * refresh keeps it, and the link can be handed to a colleague.
 *
 *   ?status=   one PaymentOrderStatus (quick chips) — deliberately NO default:
 *              a default of `captured` used to hide exactly the failed attempts
 *              an operator opens this page to find.
 *   ?stage= ?reason=   advanced filters
 *   ?gateway=  razorpay | google_play (client-side on the loaded rows: the API has no param)
 *   ?q=        @handle, order id or payment id      ?userId=  one user's attempts
 *   ?from= ?to=   YYYY-MM-DD (local days, inclusive)
 *   ?order=    the open recharge (pushed, so Back closes the drawer)
 */
export function RefundsPage() {
  const { message } = App.useApp();
  const qc = useQueryClient();
  const { get, set } = useUrlState();

  const status = oneOf(get('status'), ALL_STATUSES);
  const stage = oneOf(get('stage'), STAGES);
  const failureCode = oneOf(get('reason'), FAILURE_CODES);
  const gateway = oneOf(get('gateway'), GATEWAYS);
  const userIdFilter = get('userId') ?? '';
  const q = get('q') ?? '';
  const from = dayParam(get('from'));
  const to = dayParam(get('to'));
  const openId = get('order');

  // The search box is typed locally and committed to the URL once typing pauses,
  // so neither the URL nor the API sees every keystroke.
  const [qInput, setQInput] = useState(q);
  const qDebounced = useDebounced(qInput.trim(), 400);
  useEffect(() => {
    if (qDebounced !== q) set({ q: qDebounced });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [qDebounced]);
  useEffect(() => setQInput(q), [q]);

  const [refundOrder, setRefundOrder] = useState<OrderRow | PaymentDetail | null>(null);
  const [reason, setReason] = useState('');

  /** Everything except `status`, as GET /admin/payments/orders takes it. */
  const baseParams = useMemo(
    () => ({
      stage,
      failureCode,
      userId: userIdFilter || undefined,
      q: q || undefined,
      // Whole days, inclusive, in the operator's own timezone — sent as instants.
      since: from ? new Date(`${from}T00:00:00`).toISOString() : undefined,
      until: to ? new Date(`${to}T23:59:59.999`).toISOString() : undefined,
    }),
    [stage, failureCode, userIdFilter, q, from, to],
  );

  const list = useInfiniteQuery({
    queryKey: ['admin', 'payments', status, baseParams],
    queryFn: async ({ pageParam }) =>
      (
        await api.get<OrdersPage>('/admin/payments/orders', {
          params: { ...baseParams, status, cursor: pageParam, limit: PAGE_SIZE },
        })
      ).data,
    initialPageParam: undefined as string | undefined,
    getNextPageParam: (last) => last.nextCursor ?? undefined,
  });

  // One row per status chip: how many attempts match the other filters. A
  // `limit=1` page carries the same `filtered` count as the full list, so nine
  // tiny requests answer "how many failed in this range" without a new endpoint.
  const chipCounts = useQueries({
    queries: ALL_STATUSES.map((s) => ({
      queryKey: ['admin', 'payments', 'count', s, baseParams],
      queryFn: async () =>
        (await api.get<OrdersPage>('/admin/payments/orders', { params: { ...baseParams, status: s, limit: 1 } })).data
          .filtered,
      staleTime: 30_000,
    })),
  });
  const countFor = (s: OrderStatus | undefined): number | undefined => {
    if (s === undefined) return list.data?.pages[0]?.filtered ?? undefined;
    return chipCounts[ALL_STATUSES.indexOf(s)]?.data;
  };

  // New attempts arrive while paging, which shifts rows between pages — drop
  // repeats rather than rendering an order twice.
  const rows = useMemo(() => {
    const seen = new Set<string>();
    const out: OrderRow[] = [];
    for (const page of list.data?.pages ?? []) {
      for (const o of page.items) {
        if (seen.has(o.id)) continue;
        seen.add(o.id);
        if (gateway && o.gateway !== gateway) continue;
        out.push(o);
      }
    }
    return out;
  }, [list.data, gateway]);

  const counts = list.data?.pages[0];
  const isFiltered = Boolean(status || stage || failureCode || userIdFilter || q || from || to || gateway);

  const detail = useQuery({
    queryKey: ['admin', 'payment-detail', openId],
    queryFn: async () => (await api.get<PaymentDetail>(`/admin/payments/orders/${openId}`)).data,
    enabled: !!openId,
  });

  const refund = useMutation({
    mutationFn: async () => {
      if (!refundOrder) return;
      await api.post(`/admin/payments/${refundOrder.id}/refund`, { reason });
    },
    onSuccess: () => {
      message.success('Refund requested. Wallet credit reverses when Razorpay confirms.');
      const id = refundOrder?.id;
      setRefundOrder(null);
      setReason('');
      void qc.invalidateQueries({ queryKey: ['admin', 'payments'] });
      if (id) void qc.invalidateQueries({ queryKey: ['admin', 'payment-detail', id] });
    },
    onError: (e: unknown) => message.error(errMsg(e, 'Refund failed')),
  });

  const openOrder = (id: string) => set({ order: id }, { push: true });
  const closeOrder = () => set({ order: null });
  const clearAll = () =>
    set({ status: null, stage: null, reason: null, gateway: null, userId: null, q: null, from: null, to: null });

  const refundable = (o: { status: OrderStatus; gateway?: string; provider?: string }) =>
    o.status === 'captured' && (o.gateway ?? o.provider) === 'razorpay';

  const columns: TableColumnsType<OrderRow> = [
    {
      title: 'When',
      dataIndex: 'createdAt',
      render: (s: string) => new Date(s).toLocaleString(),
      width: 170,
    },
    {
      title: 'User',
      render: (_: unknown, r) => <UserName handle={r.userHandle} displayName={r.userDisplayName} userId={r.userId} />,
    },
    {
      title: 'Amount',
      dataIndex: 'amountPaise',
      render: (p: string) => formatRupees(p),
      width: 100,
    },
    {
      title: 'Recharge',
      render: (_: unknown, r) =>
        r.coins != null ? (
          <span>
            {r.coins.toLocaleString('en-IN')} coins
            {r.bonusCoins > 0 && (
              <Typography.Text type="secondary" style={{ fontSize: 11 }}>
                {' '}
                (incl. {r.bonusCoins.toLocaleString('en-IN')} bonus)
              </Typography.Text>
            )}
            {r.packageName && <div style={{ fontSize: 11, opacity: 0.65 }}>{r.packageName}</div>}
          </span>
        ) : (
          <Typography.Text type="secondary">—</Typography.Text>
        ),
      width: 180,
    },
    {
      title: 'Status',
      dataIndex: 'status',
      render: (v: OrderStatus) => (
        <Tooltip title={STATUS_HELP[v]}>
          <Tag color={STATUS_COLOR[v]}>{STATUS_LABEL[v]}</Tag>
        </Tooltip>
      ),
      width: 150,
    },
    {
      title: 'Service',
      dataIndex: 'gateway',
      render: (g: Gateway, row) =>
        g === 'google_play' ? 'Google Play' : row.ucb ? 'Razorpay (UCB)' : 'Razorpay',
      width: 120,
    },
    {
      title: 'Step reached',
      dataIndex: 'stage',
      render: (v: PaymentStage) => <Tag color={stageColor(v)}>{STAGE_LABEL[v] ?? v}</Tag>,
      width: 190,
      responsive: ['lg'],
    },
    {
      title: 'Why not credited',
      dataIndex: 'failureReason',
      render: (v: string | null, r) =>
        r.failureCode || v ? (
          <Tooltip
            title={[v, r.providerErrorCode && `Provider code: ${r.providerErrorCode}`].filter(Boolean).join(' · ')}
          >
            <div style={{ fontSize: 11 }}>
              {r.failureCode && <Typography.Text code>{reasonCodeText(r.failureCode)}</Typography.Text>}
              <Typography.Text style={{ fontSize: 11, display: 'block' }} ellipsis>
                {v ?? (r.failureCode ? FAILURE_LABEL[r.failureCode] : '')}
              </Typography.Text>
            </div>
          </Tooltip>
        ) : r.status === 'captured' || r.status === 'refunded' ? (
          <Typography.Text type="secondary">—</Typography.Text>
        ) : (
          <Typography.Text type="secondary" style={{ fontSize: 11 }}>
            still unresolved
          </Typography.Text>
        ),
      width: 220,
    },
    {
      title: 'Order ref',
      dataIndex: 'gatewayOrderId',
      render: (s: string) => (
        <Typography.Text code copyable={{ text: s }} style={{ fontSize: 11 }}>
          {s.length > 18 ? `${s.slice(0, 18)}…` : s}
        </Typography.Text>
      ),
      responsive: ['xl'],
    },
    {
      title: '',
      key: 'actions',
      render: (_: unknown, r) => (
        <Space size={6}>
          <Button size="small" onClick={() => openOrder(r.id)}>
            Open
          </Button>
          {refundable(r) ? (
            <Can permission="payments.refund">
              <Button danger size="small" onClick={() => setRefundOrder(r)}>
                Refund
              </Button>
            </Can>
          ) : null}
        </Space>
      ),
      width: 150,
    },
  ];

  const chip = (s: OrderStatus | undefined, label: string) => {
    const n = countFor(s);
    const active = status === s;
    return (
      <Tag.CheckableTag
        key={s ?? 'all'}
        checked={active}
        onChange={() => set({ status: active ? null : s })}
        style={{ fontSize: 13, padding: '3px 10px', border: '1px solid rgba(255,255,255,0.12)' }}
      >
        {label}
        {n !== undefined && (
          <Typography.Text type={active ? undefined : 'secondary'} style={{ marginInlineStart: 6, fontSize: 12 }}>
            {n.toLocaleString('en-IN')}
          </Typography.Text>
        )}
      </Tag.CheckableTag>
    );
  };

  return (
    <Space direction="vertical" size="large" style={{ width: '100%' }}>
      <Typography.Title level={3} style={{ color: 'white', margin: 0 }}>
        Recharges
      </Typography.Title>
      <Card>
        {/* Quick filters: one tap per status, with how many match the other filters. */}
        <Space size={[6, 8]} wrap style={{ marginBottom: 12 }} data-testid="status-chips">
          {chip(undefined, 'All')}
          {chip('captured', STATUS_LABEL.captured)}
          {chip('paid_credit_pending', STATUS_LABEL.paid_credit_pending)}
          {chip('attempted', STATUS_LABEL.attempted)}
          {chip('created', STATUS_LABEL.created)}
          {chip('failed', STATUS_LABEL.failed)}
          {chip('cancelled', STATUS_LABEL.cancelled)}
          {chip('abandoned', STATUS_LABEL.abandoned)}
          {chip('refunded', STATUS_LABEL.refunded)}
        </Space>
        <Space style={{ marginBottom: 12 }} wrap>
          <Input.Search
            allowClear
            aria-label="Search recharges"
            placeholder="Search name, @handle, order id or payment id"
            value={qInput}
            onChange={(e) => setQInput(e.target.value)}
            onSearch={(v) => set({ q: v.trim() })}
            style={{ minWidth: 320 }}
          />
          <DatePicker.RangePicker
            aria-label="Recharge date range"
            // Keyed on the URL value so a range cleared from the tag also clears the picker.
            key={`${from}|${to}`}
            defaultValue={undefined}
            onChange={(_dates, [f, t]) => set({ from: f || null, to: t || null })}
          />
          {(from || to) && (
            <Tag closable color="blue" onClose={() => set({ from: null, to: null })}>
              {from === to ? from : `${from || '…'} → ${to || '…'}`}
            </Tag>
          )}
          <Select<Gateway | undefined>
            allowClear
            aria-label="Payment service"
            placeholder="Service"
            value={gateway}
            onChange={(v) => set({ gateway: v ?? null })}
            style={{ minWidth: 140 }}
            options={[
              { value: 'razorpay', label: 'Razorpay' },
              { value: 'google_play', label: 'Google Play' },
            ]}
          />
          {userIdFilter && (
            <Tag closable color="blue" onClose={() => set({ userId: null })}>
              One user&apos;s attempts ·{' '}
              <UserName
                handle={rows[0]?.userId === userIdFilter ? rows[0]?.userHandle : undefined}
                displayName={rows[0]?.userId === userIdFilter ? rows[0]?.userDisplayName : undefined}
                userId={userIdFilter}
                secondary="inline"
              />
            </Tag>
          )}
          {isFiltered && (
            <Button type="link" size="small" onClick={clearAll}>
              Clear filters
            </Button>
          )}
        </Space>
        <Collapse
          ghost
          size="small"
          style={{ marginBottom: 8 }}
          defaultActiveKey={stage || failureCode ? ['adv'] : []}
          items={[
            {
              key: 'adv',
              label: (
                <Typography.Text type="secondary">
                  Advanced filters{stage || failureCode ? ' · active' : ''}
                </Typography.Text>
              ),
              children: (
                <Space wrap>
                  <Select<PaymentStage | undefined>
                    allowClear
                    aria-label="Stage"
                    placeholder="Step reached"
                    value={stage}
                    onChange={(v) => set({ stage: v ?? null })}
                    style={{ minWidth: 220 }}
                    options={STAGES.map((v) => ({ value: v, label: STAGE_LABEL[v] }))}
                  />
                  <Select<FailureCode | undefined>
                    allowClear
                    showSearch
                    aria-label="Reason code"
                    placeholder="Reason code"
                    value={failureCode}
                    onChange={(v) => set({ reason: v ?? null })}
                    style={{ minWidth: 260 }}
                    options={FAILURE_CODES.map((v) => ({
                      value: v,
                      label: `${reasonCodeText(v)} — ${FAILURE_LABEL[v]}`,
                    }))}
                  />
                </Space>
              ),
            },
          ]}
        />
        {counts && (
          <Space size={8} style={{ marginBottom: 12 }} wrap>
            <Tag color="blue">All attempts: {counts.total.toLocaleString('en-IN')}</Tag>
            {isFiltered && <Tag color="geekblue">Matching: {counts.filtered.toLocaleString('en-IN')}</Tag>}
            <Typography.Text type="secondary" style={{ fontSize: 12 }}>
              Showing {rows.length.toLocaleString('en-IN')} · only{' '}
              <Tag color="success" style={{ marginInlineEnd: 0 }}>
                Success
              </Tag>{' '}
              credited coins
            </Typography.Text>
          </Space>
        )}
        {list.isError && !list.data ? (
          <QueryError error={list.error} what="recharge history" onRetry={() => void list.refetch()} />
        ) : (
          <>
            <Table
              rowKey="id"
              loading={list.isLoading}
              dataSource={rows}
              columns={columns}
              onRow={(r) => ({ onDoubleClick: () => openOrder(r.id) })}
              locale={{
                emptyText: isFiltered
                  ? 'No recharge attempts match these filters.'
                  : 'No recharge attempts yet.',
              }}
              pagination={{ pageSize: 25, showSizeChanger: true, pageSizeOptions: [25, 50, 100] }}
              scroll={{ x: 1400 }}
            />
            {list.hasNextPage && (
              <div style={{ padding: 12, textAlign: 'center' }}>
                <Button loading={list.isFetchingNextPage} onClick={() => void list.fetchNextPage()}>
                  Load more ({rows.length.toLocaleString('en-IN')} loaded)
                </Button>
              </div>
            )}
          </>
        )}
      </Card>

      <Drawer
        width={960}
        open={!!openId}
        onClose={closeOrder}
        title={detail.data ? `Recharge ${detail.data.id.slice(0, 8)}` : 'Recharge'}
        extra={
          detail.data && refundable(detail.data) ? (
            <Can permission="payments.refund">
              <Button danger onClick={() => setRefundOrder(detail.data)}>
                Refund
              </Button>
            </Can>
          ) : null
        }
      >
        {detail.data ? (
          <Space direction="vertical" size={16} style={{ width: '100%' }}>
            <RechargeUserCard
              userId={detail.data.user.id}
              onShowAllAttempts={() => set({ userId: detail.data.user.id, order: null, status: null })}
            />
            <PaymentDetailView payment={detail.data} />
          </Space>
        ) : detail.isError ? (
          <QueryError error={detail.error} what="this recharge" onRetry={() => void detail.refetch()} />
        ) : detail.isLoading ? (
          <Typography.Text type="secondary">Loading recharge…</Typography.Text>
        ) : null}
      </Drawer>

      <Modal
        title={
          refundOrder
            ? `Refund ${formatRupees(refundOrder.amountPaise)} to ${
                'userHandle' in refundOrder && refundOrder.userHandle
                  ? userText({ displayName: refundOrder.userDisplayName, handle: refundOrder.userHandle })
                  : 'user' in refundOrder
                    ? userText(refundOrder.user)
                    : 'the user'
              }`
            : 'Refund'
        }
        open={!!refundOrder}
        onCancel={() => {
          setRefundOrder(null);
          setReason('');
        }}
        onOk={() => refund.mutate()}
        okText="Issue refund"
        okButtonProps={{
          danger: true,
          disabled: reason.trim().length < 10,
          loading: refund.isPending,
        }}
      >
        <Space direction="vertical" style={{ width: '100%' }}>
          <Alert
            type="warning"
            showIcon
            message="Full refund only"
            description="The coins credited by this recharge are taken back when Razorpay confirms (refund.processed webhook, usually under 30 s). If the user has already spent them the wallet can go negative — check the balance in the drawer first."
          />
          <Input.TextArea
            rows={3}
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            placeholder="Reason (≥ 10 chars) — written to audit log"
          />
        </Space>
      </Modal>
    </Space>
  );
}
